import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { type FC, type PropsWithChildren, useEffect } from 'react';
import { PlanDetail } from '@/app/components/shared/PlanDetail';
import { PromoBannerCarousel } from '@/app/components/shared/PromoBannerCarousel';
import { Dialog, DialogContent, DialogTrigger } from '@/app/components/ui/dialog';
import { SITE } from '@/app/constants';
import { useAdsDialog, useFetchProductList } from '@/app/hooks';
import { PlanDialogScene } from '@/app/types';

// PlanDialogScene 定义在 types 层，此处 re-export 以兼容既有从本模块引用的调用方
export { PlanDialogScene };

/** 各来源场景对应的标题文案。集中映射，避免条件链写两遍导致错配。 */
const SCENE_TITLE_MAP = {
  [PlanDialogScene.Default]: 'payments.plans.title',
  [PlanDialogScene.AgentProject]: 'payments.plans.titleAgentProject',
  [PlanDialogScene.InsufficientCredits]: 'payments.plans.titleInsufficientCredits',
  [PlanDialogScene.Download]: 'payments.plans.titleDownload',
} satisfies Record<PlanDialogScene, string>;

export interface PlanDetailDialogProps {
  open?: boolean;
  onClose?: () => void;
  /** 来源场景，决定文案与关闭挽留逻辑，默认 Default */
  scene?: PlanDialogScene;
  /** 去水印下载场景：是否带水印 */
  withWatermark?: boolean;
  /** 创作 ID（去水印下载场景需要） */
  creationId?: string;
  /** 弹窗来源 from_page：随 vip_price_click 上报为 modal_from_page，标识由哪个入口打开套餐弹窗 */
  fromPage?: string;
}

export const PlanDetailDialog: FC<PropsWithChildren<PlanDetailDialogProps>> = ({
  open,
  onClose,
  children,
  scene = PlanDialogScene.Default,
  creationId,
  withWatermark = false,
  fromPage,
}) => {
  const t = useTranslations();

  const titleKey = SCENE_TITLE_MAP[scene];

  const {
    loading: loadingProducts,
    error,
    getProductsList,
    subscriptionProducts,
    subscriptionTabList,
    subscriptionInitialPeriod,
    subscriptionPaidTrialInfo,
  } = useFetchProductList();

  const { tryOpenNoWatermarkDialog, tryOpenInsufficientCreditsDialog } = useAdsDialog();

  useEffect(() => {
    if (open) {
      getProductsList();
    }
  }, [open]);

  const handleClose = async () => {
    let opened = false;

    if (scene === PlanDialogScene.Download && creationId && !withWatermark) {
      // 去水印下载场景：需要有 creationId 才能触发广告挽留弹窗（批量下载场景无 creationId，不触发）
      opened = await tryOpenNoWatermarkDialog(creationId, onClose);
    } else if (scene === PlanDialogScene.InsufficientCredits) {
      // 积分不足场景：免费用户弹出广告挽留弹窗
      opened = await tryOpenInsufficientCreditsDialog(onClose);
    }

    // 如果挽留弹窗未能打开，则直接关闭订阅弹窗
    if (!opened) {
      onClose?.();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="h-[calc(100vh-88px)] max-w-[1168px] overflow-hidden rounded-[24px] border-[0.5px] border-[rgba(255,255,255,0.24)] bg-[#0F0F11] bg-center bg-cover bg-no-repeat p-0 sm:p-0 md:p-0 lg:h-full lg:max-h-full lg:w-full lg:p-0"
        style={{
          backgroundImage:
            SITE === 'china'
              ? 'url(https://image01-aliyun.vidu.zone/vidu/media-asset/plan_dialog-bg-c458af20.webp)'
              : 'url(https://image01.cf.vidu.studio/vidu/media-asset/plan_dialog-bg-c458af20.webp)',
        }}
        variant="mobileFullScreen"
        closable={true}
        closeClassName="text-system-white"
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mx-auto flex h-full w-full max-w-full flex-col overflow-y-scroll p-10 md:w-screen md:px-4 md:pt-4"
        >
          <PromoBannerCarousel className="mt-0 mb-4 flex-shrink-0" paidTrialInfo={subscriptionPaidTrialInfo} />
          <div className="select-none text-center">
            <div className="mx-auto text-center font-semibold text-[32px] text-white leading-none sm:text-left sm:text-[24px] sm:leading-tight">
              {t(titleKey)}
            </div>
          </div>
          <PlanDetail
            source={'dialog'}
            period={subscriptionInitialPeriod}
            loadingProducts={loadingProducts}
            products={subscriptionProducts}
            tabList={subscriptionTabList}
            error={error}
            paidTrialInfo={subscriptionPaidTrialInfo}
            modalFromPage={fromPage}
          />
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};
